import { useEffect } from 'react';
import { connect } from 'react-redux';
import ApprovalExpiredView from './approvalExpiredView/ApprovalExpiredView';
import MainContentPending from '../pageStructure/mainContentPending';
import { useQuery } from '@tanstack/react-query';
import {
    fetchGetApplication,
    fetchGetFormalOffers
} from '@iwoca/lapi-client/edge';
import DeclinedView from './declinedView/DeclinedView';
import { getOfferStatus, TOfferStatus } from './getOfferStatus';
import { PendingView } from './pendingView/PendingView';
import DeferredView from './deferredView/DeferredView';
import DefaultView from './defaultView/DefaultView';
import ApprovedView from './approvedView/ApprovedView';
import { Dispatch } from 'redux';
import { ACTIVATE_MY_OFFERS } from './MyOffers.actions';
import FallbackErrorModal from '../modals/fallbackErrorModal/FallbackErrorModal';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import RevokedView from './revokedView/RevokedView';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { getFormalOfferIds } from './getFormalOfferIds';
import { tracking } from '@iwoca/frontend-tracking-library';
import { ApplicationPendingView } from './pendingView/applicationPendingView/ApplicationPendingView';
import { trackPageView } from '../../tracking/pageView';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    activateMyOffers: () => dispatch({ type: ACTIVATE_MY_OFFERS })
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

const MyOffers = ({ activateMyOffers }: PropsFromRedux) => {
    //Need to activate my Offers to trigger sidebar active state
    useEffect(() => {
        activateMyOffers();
    }, [activateMyOffers]);

    const stateKey = useStateKey();

    const {
        isLoading: getFormalOffersResponseLoading,
        isError: getFormalOffersResponseError,
        data: getFormalOffersResponse
    } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        enabled: Boolean(stateKey)
    });

    const {
        isLoading: getApplicationResponseLoading,
        isError: getApplicationResponseError,
        data: getApplicationResponse
    } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey }),
        enabled: Boolean(stateKey)
    });

    const {
        isLoading: getAccountsResponseLoading,
        isError: getAccountsResponseError,
        data: getAccountsResponse
    } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        enabled: Boolean(stateKey)
    });

    const loading =
        getApplicationResponseLoading ||
        getFormalOffersResponseLoading ||
        getAccountsResponseLoading;
    const error =
        getApplicationResponseError ||
        getFormalOffersResponseError ||
        getAccountsResponseError;

    let offerStatus: TOfferStatus | null = null;

    useEffect(() => {
        //amplitude tracking
        if (
            getFormalOffersResponse &&
            getApplicationResponse &&
            getApplicationResponse.data &&
            getFormalOffersResponse.data &&
            offerStatus
        ) {
            const formalOfferIds = getFormalOfferIds(getFormalOffersResponse);

            tracking.ampli.myOffersVisited({
                formal_offer_ids: formalOfferIds,
                application_status: getApplicationResponse!.data!.status,
                page_status: offerStatus
            });
        }
    }, [getApplicationResponse, getFormalOffersResponse, offerStatus]);

    useEffect(() => {
        if (!loading) {
            trackPageView(stateKey, 'My Offers');
        }
    }, [stateKey, loading]);

    if (loading || error) {
        return <MainContentPending data-testid="my-offers-loading" />;
    }
    if (error) {
        return <FallbackErrorModal />;
    }

    offerStatus = getOfferStatus(
        getFormalOffersResponse!,
        getApplicationResponse!,
        getAccountsResponse!
    );

    switch (offerStatus) {
        case 'application_declined':
            return <DeclinedView />;
        case 'application_pending':
            return getCountry() === 'UK' ? (
                <ApplicationPendingView />
            ) : (
                <PendingView />
            );
        case 'application_deferred':
            return <DeferredView />;
        case 'offer_expired':
            return <ApprovalExpiredView />;
        case 'application_offer_revoked':
            return getCountry() === 'DE' ? <RevokedView /> : <DefaultView />;
        case 'offered':
            return <ApprovedView />;
        case 'no_offer':
            return <DefaultView />;
        default:
            return <DefaultView />;
    }
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(MyOffers);
